import toast from "react-hot-toast";
import { Action, CombinedState, ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import { Models, IndexedDB, Services, Utils, Constants, Types, Interfaces, Logger } from "@sendum/core";
import { IVisibilityState } from "./VisibilityReducer";
import { DispatchAction, VisibilityRootState } from "./VisibilityStore";
import { AnyAction } from "redux";

const logger = new Logger("VisibilityActions");
const FULL_LEGS_LIST_EtE = ["Placement", "Live", "Return"];
const VIEW_A1 = "A1"; // Marken view
const VIEW_A2 = "A2"; // Kite view
const VIEW_A3 = "A3"; // Hospital view

let isFirstGetHistoryPublicRequest = true;

type TDispatch = ThunkDispatch<CombinedState<{
	Visibility: IVisibilityState;
}>, null, Action<string>>;

const getHistoryAsync = async (state: IVisibilityState, dispatch: TDispatch, shipmentId: string, deviceId: string, deviceType: string, returnedDate: number | undefined, sensor: string, view = "", orderId = "", virtualShipmentName = ""): Promise<void> => {
	const key = `${shipmentId}#${deviceId}#${sensor}`;

	if (state.historyLoading.get(key)) {
		return;
	}

	await dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: true }) as unknown as AnyAction);
	const sensorKey = IndexedDB.Sensor.buildKey(shipmentId, deviceId, sensor, view, orderId);
	const sensorFormIDB: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey);
	const startDate: number = sensorFormIDB?.lastEpoch || 0;
	const endDate: number = Date.now();
	if (endDate - startDate > Utils.time.MILLISECONDS_OF_5MIN) {
		const sensorDBKey: string = Models.parseSensor(sensor, deviceType, false);
		await getHistoryRecursively(shipmentId, deviceId, sensorDBKey, startDate, endDate, deviceType, view, orderId).then();
	}

	const sensorHistory = await new IndexedDB.SensorHistory().getAll(shipmentId, deviceId, sensor, returnedDate);
	const { lastEpoch }: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey) || {};
	const maxEpoch: number = Math.max(...sensorHistory.map(_ => _.epoch || 0));
	let sensorData = {};

	if (maxEpoch > startDate) {
		if (!lastEpoch || lastEpoch < startDate || lastEpoch > endDate || maxEpoch > lastEpoch) {
			sensorData = { lastEpoch: maxEpoch };
		}
	}

	await dispatch(addHistory(deviceId, virtualShipmentName || shipmentId, sensorHistory as Types.TProperty[], sensor) as unknown as AnyAction);
	dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: false }) as unknown as AnyAction);
	await new IndexedDB.Sensor().update(sensorKey, {...sensorData, lastUsage: Utils.time.current() });
};

const getSensors = (shipment: Models.Shipment, deviceId?: string): string[] => {
	if (shipment.devices?.length) {
		const deviceIndex = shipment.devices.findIndex((d: Models.Device) => deviceId && d?.id === deviceId);
		const device = deviceIndex !== Constants.BAD_INDEX ? shipment.devices[deviceIndex] : shipment.devices[0];
		if (device) {
			const sensors = [...Array.from(device.getAttributesConfig().keys()), ...(device?.getHistoryTableConfig().additionalSensors ?? [])];
			if (!sensors.includes("location")) {
				sensors.push("location");
			}

			return sensors;
		}
	}

	return [];
};

export enum VisibilityActionTypes {
	SET_LOADING_SHIPMENT,
	SET_LOADING_SHIPMENTS,
	SET_LOADING_HISTORY,
	SET_SHIPMENT,
	SET_SHIPMENTS,
	SET_DEVICE,
	SET_CURRENT_DEVICE_ID,
	UPDATE_ALARM,
	ADD_HISTORY,
	CLEAR_HISTORY,
	SET_SENSORS,
	SET_MULTIPLE_PLACE_COMPONENTS,
	SET_ERROR,
	UPDATE_STATE_BY_MQTT,
	SET_RULES,
	RESET_STATE,
	SET_LOADING_RULES,
	SET_SELECTED_SENSOR,
	RESET_SELECTED_SENSOR,
	RESET_IS_CLEAR_ALL_ALARMS,
	SET_IS_CLEAR_ALL_ALARMS,
	SET_IS_VIRTUAL_SHIPMENT,
	SET_ORDER_ID,
	SET_TRACKING_TIMES,
	UPDATE_DEVICE_PRESENT,
	SET_IS_UPDATE_DEVICE_PRESENT,
	SET_VIEW,
	SET_LEGS,
	SET_IS_LIVE_LEG_EXTENDED,
}

export type TAction = {
	isLoadingShipment?: boolean;
	isLoadingShipments?: boolean;
	deviceId?: string;
	shipmentId?: string;
	sensor?: string;
	isLoadingHistory?: boolean;
	isLoadingRules?: boolean;
	shipment?: Models.Shipment;
	shipments?: Models.Shipment[];
	device?: Models.Device;
	currentDeviceId?: string;
	alarm?: Models.Alarm;
	sensorHistory?: Types.TProperty[];
	sensors?: string[];
	rules?: Models.Rules;
	component?: "graph" | "reports" | "rules" | "alarms" | "map";
	error?: Error;
	message?: string;
	topic?: string;
	isVirtual?: boolean;
	orderId?: string;
	start?: number;
	end?: number;
	present?: Map<string, Types.TProperty>;
	isUpdateDevicePresent?: boolean;
	view?: string;
	legs?: string[];
	isLiveLegExtended?: boolean;
};

export const resetState = (): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.RESET_STATE,
	payload: {},
});

export const setLoadingShipment = (isLoadingShipment: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_LOADING_SHIPMENT,
	payload: {
		isLoadingShipment,
	},
});

export const setLoadingShipments = (isLoadingShipments: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_LOADING_SHIPMENTS,
	payload: {
		isLoadingShipments,
	},
});

export const setLoadingHistory = (payload: { deviceId: string; shipmentId: string; sensor: string; isLoadingHistory: boolean }): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_LOADING_HISTORY,
	payload,
});

export const setLoadingRules = (deviceId: string, shipmentId: string, isLoadingRules: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_LOADING_RULES,
	payload: {
		deviceId,
		shipmentId,
		isLoadingRules,
	},
});

export const setShipment = (shipment: Models.Shipment, deviceId?: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_SHIPMENT,
	payload: {
		shipment,
		deviceId,
	},
});

export const setShipments = (shipments: Models.Shipment[]): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_SHIPMENTS,
	payload: {
		shipments,
	},
});

export const setDevice = (device: Models.Device): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_DEVICE,
	payload: {
		device,
		currentDeviceId: device.id,
	},
});

export const setCurrentDeviceId = (deviceId: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_CURRENT_DEVICE_ID,
	payload: {
		currentDeviceId: deviceId,
	},
});

export const updateAlarms = (deviceId: string, alarm: Models.Alarm): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.UPDATE_ALARM,
	payload: {
		deviceId,
		alarm,
	},
});

export const resetAllAlarmsClear = (): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.RESET_IS_CLEAR_ALL_ALARMS,
	payload: {},
});

export const setAllAlarmsClear = (): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_IS_CLEAR_ALL_ALARMS,
	payload: {},
});

export const addHistory = (deviceId: string, shipmentId: string, sensorHistory: Types.TProperty[], sensor: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.ADD_HISTORY,
	payload: {
		deviceId,
		shipmentId,
		sensorHistory,
		sensor,
	},
});

export const clearHistory = (): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.CLEAR_HISTORY,
	payload: {},
});

export const setSensors = (sensors: string[]): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_SENSORS,
	payload: {
		sensors,
	},
});

export const resetSelectedSensor = (sensors: string[]): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.RESET_SELECTED_SENSOR,
	payload: {
		sensors,
	},
});

export const setSelectedSensor = (sensor: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_SELECTED_SENSOR,
	payload: {
		sensor,
	},
});

export const setRules = (rules: Models.Rules): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_RULES,
	payload: {
		rules,
	},
});

export const setMultiplePlaceComponent = (component: "graph" | "reports" | "rules" | "alarms" | "map"): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_MULTIPLE_PLACE_COMPONENTS,
	payload: {
		component,
	},
});

export const setError = (error: Error): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_ERROR,
	payload: {
		error,
	},
});

export const updateStateByMqtt = (message: string, topic: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.UPDATE_STATE_BY_MQTT,
	payload: {
		message,
		topic,
	},
});

export const setIsVirtual = (isVirtual: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_IS_VIRTUAL_SHIPMENT,
	payload: {
		isVirtual,
	},
});

export const setOrderId = (orderId: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_ORDER_ID,
	payload: {
		orderId,
	},
});

export const setTrackingTimes = (start?: number, end?: number): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_TRACKING_TIMES,
	payload: {
		start,
		end,
	},
});

export const updateDevicePresent = (deviceId: string, shipmentId: string, present: Map<string, Types.TProperty>, sensors: string[]): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.UPDATE_DEVICE_PRESENT,
	payload: {
		deviceId,
		shipmentId,
		present,
		sensors,
	},
});

export const setIsUpdateDevicePresent = (isUpdateDevicePresent: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_IS_UPDATE_DEVICE_PRESENT,
	payload: {
		isUpdateDevicePresent,
	},
});

export const setView = (view: string): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_VIEW,
	payload: {
		view,
	},
});

export const setLegs = (legs: string[]): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_LEGS,
	payload: {
		legs,
	},
});

export const setIsLiveLegExtended = (isLiveLegExtended: boolean): DispatchAction<TAction> => ({
	type: VisibilityActionTypes.SET_IS_LIVE_LEG_EXTENDED,
	payload: {
		isLiveLegExtended,
	},
});

export const getShipments = (name: string): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	const SHIPMENTS_COUNT = 3;
	const state: IVisibilityState = getState().Visibility;
	await dispatch(setOrderId(name) as unknown as AnyAction);
	await dispatch(setLoadingShipments(true) as unknown as AnyAction);

	try {
		const smallShipments = await Services.API.WidgetsApi.getShipments({ view: "groups", name });
		if (smallShipments.length !== SHIPMENTS_COUNT) {
			throw new Error("Bad virtual shipment");
		}
		const shipments = await Promise.all(smallShipments.map(async smallShipment => await Services.API.WidgetsApi.getShipmentByOrderId(smallShipment.masterTrackingNo)));
		await dispatch(setShipments(shipments) as unknown as AnyAction);

		const isNotStartedAll = shipments.every(s => !!s && s.status !== "Completed" && s.status !== "InProgress");
		const isAllComplited = shipments.every(s => !!s && s.status === "Completed");
		const inProgressShipment = shipments.find(s => !!s && s.status === "InProgress");
		const placementShipment = shipments.find(s => s.leg === "Placement");
		const returnShipment = shipments.find(s => s.leg === "Return");
		if (inProgressShipment !== undefined) {
			await dispatch(setShipment(inProgressShipment) as unknown as AnyAction);
			const [device] = inProgressShipment.devices;
			if (device) {
				await dispatch(setDevice(device) as unknown as AnyAction);
			}
		}
		if (isNotStartedAll && placementShipment !== undefined) {
			await dispatch(setShipment(placementShipment) as unknown as AnyAction);
			const [device] = placementShipment.devices;
			if (device) {
				await dispatch(setDevice(device) as unknown as AnyAction);
			}
		}
		if (isAllComplited && returnShipment !== undefined) {
			await dispatch(setShipment(returnShipment));
			const [device] = returnShipment.devices;
			if (device) {
				await dispatch(setDevice(device));
			}
		}

		for (const shipment of shipments) {
			for (const device of shipment.devices) {
				const sensors = new Set(["location", ...Array.from(device.getAttributesConfig().keys()), ...(device?.getHistoryTableConfig().additionalSensors ?? [])]);
				for (const sensor of sensors) {
					await getHistoryAsync(state, dispatch, shipment.id, device.id, device.type, device.returnedDate, sensor, "", "", shipment.name);
				}
			}
		}
		await dispatch(setLoadingShipments(false));
	} catch (error) {
		logger.error(error);
		await dispatch(setLoadingShipments(false));
		dispatch(setError(error));
		if (error instanceof Error) {
			toast.error(error.message);
		}
	}
};

export const getShipment = (masterTrackingNo: string, deviceId?: string, isLiveLegExtended?: boolean): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	await dispatch(setLoadingShipment(true));
	await dispatch(setOrderId(masterTrackingNo));
	await dispatch(setView(VIEW_A1));
	await dispatch(setIsLiveLegExtended(isLiveLegExtended || false));

	try {
		const shipment = await Services.API.WidgetsApi.getShipmentByOrderId(masterTrackingNo);
		await dispatch(setShipment(shipment, deviceId));
		Services.MQTT.subscribe([`shipment/${shipment.id}`]);
		await isVirtualShipment(dispatch, shipment.id);

		await Promise.all(shipment.devices.map(async (device: Models.Device) => {
			await Promise.all(getSensors(shipment, device.id).map(async (sensor: string) => {
				await getHistoryCommon(getState().Visibility, dispatch, sensor, FULL_LEGS_LIST_EtE, VIEW_A1);
			}));
		}));

		// TODO get rules;
	} catch (error) {
		logger.error(error);
		await dispatch(setLoadingShipment(false));
		dispatch(setError(error));
		if (error instanceof Error) {
			toast.error(error.message);
		}
	}
};

export const getShipmentPublic = (masterTrackingNo: string, deviceId?: string, isLiveLegExtended?: boolean): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	await dispatch(setLoadingShipment(true));
	await dispatch(setOrderId(masterTrackingNo));
	await dispatch(setView(VIEW_A3));
	await dispatch(setIsLiveLegExtended(isLiveLegExtended || false));

	try {
		const shipment = await Services.API.WidgetsApi.getShipmentByOrderId(masterTrackingNo);
		await dispatch(setShipment(shipment, deviceId));
		Services.MQTT.subscribe([`shipment/${shipment.id}`]);
		await isVirtualShipment(dispatch, shipment.id);

		await Promise.all(getSensors(shipment, deviceId).map(async (sensor: string) => {
			await getHistoryPublic(sensor, getState().Visibility, dispatch);
		}));
		isFirstGetHistoryPublicRequest = false;
	} catch (error) {
		logger.error(error);
		await dispatch(setLoadingShipment(false));
		dispatch(setError(error));
		if (error instanceof Error) {
			toast.error(error.message);
		}
	}
};

export const getShipmentShared = (masterTrackingNo: string, deviceId?: string, isLiveLegExtended?: boolean): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	await dispatch(setLoadingShipment(true));
	await dispatch(setOrderId(masterTrackingNo));
	await dispatch(setView(VIEW_A2));
	await dispatch(setIsLiveLegExtended(isLiveLegExtended || false));

	try {
		const shipment = await Services.API.WidgetsApi.getShipmentByOrderId(masterTrackingNo);
		await dispatch(setShipment(shipment, deviceId));
		Services.MQTT.subscribe([`shipment/${shipment.id}`]);
		await isVirtualShipment(dispatch, shipment.id);

		// getSensors(shipment, deviceId).map(async (sensor: string) => {
		// 	await getHistoryCommon(getState().Visibility, dispatch, sensor, ["Placement", "Live"], VIEW_A2);
		// });
		await Promise.all(shipment.devices.map(async (device: Models.Device) => {
			await Promise.all(getSensors(shipment, device.id).map(async (sensor: string) => {
				await getHistoryCommon(getState().Visibility, dispatch, sensor, ["Placement", "Live"], VIEW_A2);
			}));
		}));
	} catch (error) {
		logger.error(error);
		await dispatch(setLoadingShipment(false));
		dispatch(setError(error));
		if (error instanceof Error) {
			toast.error(error.message);
		}
	}
};

const isVirtualShipment = async (dispatch: TDispatch, shipmentId: string): Promise<void> => {
	try {
		const isVirtual = await Services.API.WidgetsApi.isVirtualShipment(shipmentId);
		dispatch(setIsVirtual(isVirtual));
	} catch (ignore) {
		logger.debug(ignore);
		dispatch(setIsVirtual(false));
	}
};

// TODO getRules
// const getRules = async (dispatch: TDispatch, shipmentId: string, deviceId: string, deviceType: string): Promise<void> => {
// try {
// await dispatch(setLoadingRules(deviceId, shipmentId, true));
// const rules = await Services.API.WidgetsApi.getRules(shipmentId, deviceId, deviceType);
// await dispatch(setRules(rules));
// await dispatch(setLoadingRules(deviceId, shipmentId, false));
// } catch (e) {
// await dispatch(setLoadingRules(deviceId, shipmentId, false));
// throw e;
// }
// };

export const getHistoryAction = (sensor: string): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	const state: IVisibilityState = getState().Visibility;
	const shipmentId = state.shipment?.id;
	const deviceId = state.device?.id;
	const deviceType = state.device?.type;

	if (!shipmentId || !deviceId || !deviceType) {
		return;
	}

	try {
		await getHistoryAsync(state, dispatch, shipmentId, deviceId, deviceType, state.device?.returnedDate, sensor);
	} catch (err) {
		logger.error(err);
		dispatch(setError(err));
	}
};

export const getHistoryPublicInterval = (shipmentSegments: Models.Segment[]): { startTime: number; endTime: number; inProgress: boolean } => {
	const MINIMAL_SEGMENTS_COUNT = 3;
	let startTime = 0;
	let endTime = 0;
	const inProgress = false;
	if (shipmentSegments.length >= MINIMAL_SEGMENTS_COUNT) {
		const segment = shipmentSegments[shipmentSegments.length - MINIMAL_SEGMENTS_COUNT];
		if (segment?.status === "InProgress" || segment?.status === "Completed") {
			segment.milestones.forEach(milestone => {
				const milestoneTime = milestone.actualEpochTime;
				if (milestoneTime) {
					if (!startTime && !endTime) {
						startTime = milestoneTime;
						endTime = milestoneTime;
					} else {
						startTime = milestoneTime < startTime ? milestoneTime : startTime;
						endTime = milestoneTime > endTime ? milestoneTime : endTime;
					}
				}
			});
			startTime = startTime || segment.start;
		}
	}

	return {
		startTime,
		endTime,
		inProgress,
	};
};

const getHistoryCommon = async (state: IVisibilityState, dispatch: TDispatch, sensor: string, legs: string[], view: string): Promise<void> => {
	logger.debug("getHistoryCommon");
	const shipmentId = state.shipment?.id;
	const deviceId = state.device?.id;
	const deviceType = state.device?.type;
	const isLiveLegExtended = state.isLiveLegExtended;

	if (!shipmentId || !deviceId || !deviceType) {
		return;
	}
	const { shipment, orderId } = state;
	if (state.shipment && (!state.shipment.leg || state.shipment.leg !== "End-to-End")) {
		try {
			await getHistoryAsync(state, dispatch, shipmentId, deviceId, deviceType, state.device?.returnedDate, sensor, view, orderId);
		} catch (err) {
			logger.error(err);
			dispatch(setError(err));
		}
	} else {
		const key = `${shipmentId}#${deviceId}#${sensor}`;

		if (state.historyLoading.get(key)) {
			return;
		}

		if (!shipment || !orderId) {
			return;
		}

		dispatch(setLegs(legs));

		const { startTime, endTime, inProgress } = Utils.getHistoryEndToEndInterval(shipment, orderId, legs, isLiveLegExtended);
		if (!inProgress) {
			dispatch(setTrackingTimes(startTime, endTime));
		} else {
			dispatch(setTrackingTimes(startTime, undefined));
		}
		if (!startTime) {
			await dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: true }));
			await dispatch(addHistory(deviceId, shipmentId, [], sensor));
			dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: false }));

			return;
		}

		await dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: true }));
		try {
			const sensorKey = IndexedDB.Sensor.buildKey(shipmentId, deviceId, sensor, view, orderId);
			const sensorFormIDB: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey);
			const startDate: number = sensorFormIDB?.lastEpoch || startTime;
			const endDate: number = endTime || Date.now();

			if (endDate - startDate > Utils.time.MILLISECONDS_OF_5MIN) {
				const sensorDBKey: string = Models.parseSensor(sensor, state.device?.type || "", false);
				await getHistoryRecursively(shipmentId, deviceId, sensorDBKey, startDate, endDate, deviceType, view, orderId).then();
			}

			const sensorhistory = await new IndexedDB.SensorHistory().getAll(shipmentId, deviceId, sensor, endDate, startTime);
			const { lastEpoch }: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey) || {};
			const maxEpoch: number = Math.max(...sensorhistory.map(_ => _.epoch || 0));
			let sensorData = {};

			if (maxEpoch > startDate) {
				if (!lastEpoch || lastEpoch < startDate || lastEpoch > endDate || maxEpoch > lastEpoch) {
					sensorData = { lastEpoch: maxEpoch };
				}
			}

			await dispatch(addHistory(deviceId, shipmentId, sensorhistory as Types.TProperty[], sensor));
			dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: false }));
			await new IndexedDB.Sensor().update(sensorKey, {...sensorData, lastUsage: Utils.time.current() });
		} catch (err) {
			dispatch(setError(err));
		}
	}
};

export const getHistoryPublic = async (sensor: string, state: IVisibilityState, dispatch: TDispatch): Promise<void> => {
	const shipmentId = state.shipment?.id;
	const deviceId = state.device?.id;
	const deviceType = state.device?.type;
	const isLiveLegExtended = state.isLiveLegExtended;

	if (!shipmentId || !deviceId || !deviceType) {
		return;
	}

	const key = `${shipmentId}#${deviceId}#${sensor}`;

	if (state.historyLoading.get(key)) {
		return;
	}

	const { shipment, orderId } = state;
	if (!shipment || !orderId) {
		return;
	}
	if (state.shipment && state.shipment.leg && state.shipment.leg === "End-to-End") {
		dispatch(setLegs(["Live"]));
	}
	const { startTime, endTime, inProgress } = state.shipment && state.shipment.leg && state.shipment.leg === "End-to-End" ?
		Utils.getHistoryEndToEndInterval(shipment, orderId, ["Live"], isLiveLegExtended) :
		getHistoryPublicInterval(shipment?.segments || []);
	if (!inProgress) {
		dispatch(setTrackingTimes(startTime, endTime));
	} else {
		dispatch(setTrackingTimes(startTime, undefined));
	}

	if (!startTime) {
		await dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: true }));
		await dispatch(addHistory(deviceId, shipmentId, [], sensor));
		dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: false }));

		return;
	}

	await dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: true }));
	try {
		const sensorKey = IndexedDB.Sensor.buildKey(shipmentId, deviceId, sensor, VIEW_A3, orderId);
		const sensorFormIDB: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey);
		const startDate: number = sensorFormIDB?.lastEpoch || startTime;
		const endDate: number = endTime || Date.now();

		if (endDate - startDate > Utils.time.MILLISECONDS_OF_5MIN || (isFirstGetHistoryPublicRequest && endDate - startDate > 0)) {
			const sensorDBKey: string = Models.parseSensor(sensor, state.device?.type || "", false);
			await getHistoryRecursively(shipmentId, deviceId, sensorDBKey, startDate, endDate, deviceType, VIEW_A3, orderId).then();
		}

		const sensorhistory = await new IndexedDB.SensorHistory().getAll(shipmentId, deviceId, sensor, endTime, startTime);
		const { lastEpoch }: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey) || {};
		const maxEpoch: number = Math.max(...sensorhistory.map(_ => _.epoch || 0));
		let sensorData = {};

		if (maxEpoch > startDate) {
			if (!lastEpoch || lastEpoch < startDate || lastEpoch > endDate || maxEpoch > lastEpoch) {
				sensorData = { lastEpoch: maxEpoch };
			}
		}

		await dispatch(addHistory(deviceId, shipmentId, sensorhistory as Types.TProperty[], sensor));
		dispatch(setLoadingHistory({ deviceId, shipmentId, sensor, isLoadingHistory: false }));
		await new IndexedDB.Sensor().update(sensorKey, {...sensorData, lastUsage: Utils.time.current() });
	} catch (err) {
		dispatch(setError(err));
	}
};

const getHistoryRecursively = async (shipmentId: string, deviceId: string, sensorDBKey: string, startDate: number, endDate: number, deviceType: string, view: string, orderId: string, lastDynamoKey?: string): Promise<void> => {
	const { history, newLastKey } = await Services.API.WidgetsApi.getDeviceHistory(shipmentId, deviceId, startDate, endDate, sensorDBKey, deviceType, lastDynamoKey);
	const data = history.get(Models.parseSensor(sensorDBKey, deviceType, true))?.filter(_ => !!_) ?? [];
	const sensorHistory: Interfaces.ISensorProperty[] = data.map(item => ({
		latitude: (item as Types.TLocationProperty)?.latitude,
		longitude: (item as Types.TLocationProperty)?.longitude,
		dataSource: (item as Types.TLocationProperty)?.dataSource,
		heading: (item as Types.TLocationProperty)?.heading,
		value: (item as Types.TNumberProperty)?.value,
		epoch: (item as Types.TNumberProperty)?.epoch,
	}));

	await new IndexedDB.SensorHistory().putBulk(sensorHistory, shipmentId, deviceId, Models.parseSensor(sensorDBKey, deviceType, true));

	const maxEpoch: number = Math.max(...sensorHistory.map(_ => _.epoch || 0));
	if (maxEpoch > startDate) {
		const sensorKey = IndexedDB.Sensor.buildKey(shipmentId, deviceId, Models.parseSensor(sensorDBKey, deviceType, true), view, orderId);
		await new IndexedDB.Sensor().update(sensorKey, { lastEpoch: maxEpoch });
	}
	const lastKey = newLastKey.get(Models.parseSensor(sensorDBKey, deviceType, true));
	if (lastKey) {
		return getHistoryRecursively(shipmentId, deviceId, sensorDBKey, maxEpoch, endDate, deviceType, view, orderId, lastKey);
	}

	return Promise.resolve();
};

export const getRulesAction = (): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	const state: IVisibilityState = getState().Visibility;
	if (!state.shipment?.id || !state.device?.id) {
		return;
	}
	const shipmentId = state.shipment?.id;
	const deviceId = state.device?.id;
	try {
		await dispatch(setLoadingRules(deviceId, shipmentId, true));
		const rules = await Services.API.WidgetsApi.getRules(shipmentId, deviceId, state.device.type);
		await dispatch(setRules(rules));
		await dispatch(setLoadingRules(deviceId, shipmentId, false));
	} catch (e) {
		await dispatch(setLoadingRules(deviceId, shipmentId, false));
		dispatch(setError(e));
	}
};

export const updatePresent = (shipmentId: string, deviceId: string, sensors: string[]): ThunkAction<void, VisibilityRootState, null, Action<string>> => async (dispatch, getState) => {
	const state: IVisibilityState = getState().Visibility;
	const present: Map<string, Types.TProperty> = new Map<string, Types.TProperty>();
	const { view } = state;
	dispatch(setIsUpdateDevicePresent(true));
	try {
		for (const sensor of sensors) {
			const sensorKey = IndexedDB.Sensor.buildKey(shipmentId, deviceId, sensor, view || "", state.orderId || "");
			const sensorFormIDB: Interfaces.ISensor = await new IndexedDB.Sensor().get(sensorKey);
			if (sensorFormIDB?.lastEpoch) {
				const sensorHistory = await new IndexedDB.SensorHistory().getAll(shipmentId, deviceId, sensor, sensorFormIDB?.lastEpoch, sensorFormIDB?.lastEpoch);
				if (sensorHistory.length) {
					delete sensorHistory[0].id;
					delete sensorHistory[0].dataSource;
					delete sensorHistory[0].heading;
					if (!sensorHistory[0].latitude) {
						delete sensorHistory[0].latitude;
						delete sensorHistory[0].longitude;
					}
					present.set(sensor, sensorHistory[0] as Types.TProperty);
				}
			} else {
				present.set(sensor, undefined);
			}
		}
		logger.debug(present);
		await dispatch(updateDevicePresent(deviceId, shipmentId, present, sensors));
		dispatch(setIsUpdateDevicePresent(false));
	} catch (e) {
		dispatch(setError(e));
	}
};
